import { useState } from "react";
import { Check, ClipboardCheck } from "lucide-react";
import { trpc } from "@/lib/trpc";
import { toast } from "sonner";
import { LEGACY360_PAYMENT_DISCLOSURE_TEXT } from "@shared/consultationEligibility";
import { withOptimisticOverride } from "@/lib/checklistOptimistic";

// ─── Legacy360 Living Trust — associate self-attestation checklist ──────────
// Legacy360 Living Trust has no client-portal document trail (unlike
// Share360), so this is the only automatable proxy for "the associate did
// the required conversational steps" per the Living Trust submission-process
// doc: educate the client, explain fees/payment-at-appointment, confirm the
// client's decision, and explicitly acknowledge the required payment/
// onboarding disclosure. Each checkbox auto-saves independently via
// consultation.setLegacy360Checklist, then invalidates checkEligibility so
// the Schedule Consultation button (RepDashboard/LeadDetailDrawer) re-checks
// immediately. Rendered only for verticalName === "Legacy360" &&
// resolveLegacy360Subtype(lead) === "living_trust" - the only Legacy360
// sub-type this gate applies to.
//
// `checklist` is a prop derived from the parent's trpc.lead.list cache, not
// fetched here. A click shows an optimistic overlay instantly, and on success
// the mutation's OWN response (already the authoritative, merged checklist -
// see consultation.setLegacy360Checklist's return value) becomes the new
// confirmed baseline immediately - it does NOT wait on invalidating/
// refetching lead.list first. That refetch is still fired in the background
// so every other view of this lead eventually catches up, but it is
// deliberately not awaited: waiting on it caused a real, reproduced bug
// (2026-08-17) where the checkbox would show the new state correctly for
// ~1 second, then visibly revert to the OLD value and stay there - the
// invalidate-triggered lead.list refetch was racing the mutation and
// resolving with pre-write data, and clearing the optimistic overlay only
// once that (stale) refetch landed made the stale value stick. Trusting the
// mutation's own response instead removes the race entirely.

type ChecklistKeys = "clientEducated" | "feesDiscussed" | "clientConfirmedDecision" | "paymentDisclosureAcknowledged";

const CHECKLIST_ITEMS: { key: Extract<ChecklistKeys, "clientEducated" | "feesDiscussed" | "clientConfirmedDecision">; label: string }[] = [
  { key: "clientEducated", label: "Client educated on the Living Trust program (benefits, objections answered)" },
  { key: "feesDiscussed", label: "Program fees explained - client understands payment is collected during the Concierge appointment" },
  { key: "clientConfirmedDecision", label: "Client has confirmed they want to move forward and are ready to schedule" },
];

export function Legacy360PreAppointmentChecklist({ leadId, checklist, readOnly = false }: {
  leadId: number;
  checklist?: { clientEducated?: boolean; feesDiscussed?: boolean; clientConfirmedDecision?: boolean; paymentDisclosureAcknowledged?: boolean } | null;
  // Not currently set by any caller - the owning rep, a real admin, and any
  // in-scope Team Portal member (any role, vertical-scoped server-side) can
  // all write this checklist. Kept as an escape hatch for a genuinely
  // view-only caller in the future rather than removed outright.
  readOnly?: boolean;
}) {
  const utils = trpc.useUtils();
  const [optimistic, setOptimistic] = useState<Partial<Record<ChecklistKeys, boolean>> | null>(null);
  // Server-confirmed checklist from the mutation's own response - once set,
  // this (not the `checklist` prop) is the baseline, since it reflects
  // exactly what was just written with no refetch/race involved. Persists
  // across re-renders so a later, unrelated prop update can't un-confirm it.
  const [confirmed, setConfirmed] = useState<Partial<Record<ChecklistKeys, boolean>> | null>(null);

  const setChecklist = trpc.consultation.setLegacy360Checklist.useMutation({
    onSuccess: (data) => {
      setConfirmed(data.checklist as Partial<Record<ChecklistKeys, boolean>>);
      setOptimistic(null);
      // Background refresh for every OTHER view of this lead - not awaited,
      // since this component's own display no longer depends on it.
      utils.consultation.checkEligibility.invalidate({ leadId });
      utils.lead.list.invalidate();
    },
    onError: (e: any) => {
      // Never keep showing a value that was never actually saved.
      setOptimistic(null);
      toast.error(e.message || "Failed to save checklist item.");
    },
  });

  const baseline = confirmed ?? checklist ?? null;
  const displayed = withOptimisticOverride<Record<ChecklistKeys, boolean>>(baseline, optimistic);
  const isChecked = (key: ChecklistKeys) => !!displayed[key];
  const paymentDisclosureAcknowledged = isChecked("paymentDisclosureAcknowledged");
  const allDone = CHECKLIST_ITEMS.every(item => isChecked(item.key)) && paymentDisclosureAcknowledged;
  // Serialize clicks against this lead's checklist - disabling every checkbox
  // while any one of them has a save in flight means the server's
  // read-existing/merge/write-whole-metadata handler never sees two
  // overlapping requests for the same lead, so it can't lose one to the
  // other in a last-write-wins race.
  const disabled = readOnly || setChecklist.isPending;

  const toggle = (key: ChecklistKeys) => {
    if (disabled) return;
    const next = !isChecked(key);
    setOptimistic({ ...displayed, [key]: next });
    setChecklist.mutate({ leadId, [key]: next } as any);
  };

  return (
    <div className="rounded-[10px] border p-3" style={{ background: allDone ? "#F0F9F4" : "#FFF9EC", borderColor: allDone ? "#CDE9D9" : "#F0DFB0" }}>
      <div className="flex items-center gap-1.5 text-[12.5px] font-semibold mb-2" style={{ color: allDone ? "#2F7A4D" : "#8A6B1F" }}>
        <ClipboardCheck className="w-3.5 h-3.5" />
        Pre-Appointment Checklist{allDone ? " - Complete" : ""}{readOnly ? " (View Only)" : ""}
      </div>
      <div className="flex flex-col gap-1.5">
        {CHECKLIST_ITEMS.map(item => {
          const checked = isChecked(item.key);
          return (
            <label key={item.key} className={`flex items-start gap-2 text-[12px] select-none ${disabled ? "cursor-default" : "cursor-pointer"}`}>
              <span
                onClick={(e) => {
                  e.preventDefault();
                  toggle(item.key);
                }}
                className="flex-shrink-0 mt-[1px] w-4 h-4 rounded-[4px] border flex items-center justify-center transition-colors"
                style={{
                  background: checked ? "#2F7A4D" : "#fff",
                  borderColor: checked ? "#2F7A4D" : "#D8D0C2",
                  opacity: disabled ? 0.75 : 1,
                }}
              >
                {checked && <Check className="w-3 h-3 text-white" />}
              </span>
              <span className={checked ? "text-[#4A5A4E]" : "text-[#6E665A]"}>{item.label}</span>
            </label>
          );
        })}
      </div>

      {/* Required verbatim disclosure - the associate must read this to the
          client and explicitly acknowledge it before scheduling. Kept
          separate from the paraphrased checklist items above so the exact
          wording the client mandated is never silently reworded. */}
      <div className="mt-3 pt-3 border-t" style={{ borderColor: allDone ? "#CDE9D9" : "#F0DFB0" }}>
        <div className="text-[11px] text-[#6E665A] bg-white/60 border border-[#EBE2D4] rounded-[8px] p-2 mb-1.5 italic">
          "{LEGACY360_PAYMENT_DISCLOSURE_TEXT}"
        </div>
        <label className={`flex items-start gap-2 text-[12px] select-none ${disabled ? "cursor-default" : "cursor-pointer"}`}>
          <span
            onClick={(e) => {
              e.preventDefault();
              toggle("paymentDisclosureAcknowledged");
            }}
            className="flex-shrink-0 mt-[1px] w-4 h-4 rounded-[4px] border flex items-center justify-center transition-colors"
            style={{
              background: paymentDisclosureAcknowledged ? "#2F7A4D" : "#fff",
              borderColor: paymentDisclosureAcknowledged ? "#2F7A4D" : "#D8D0C2",
              opacity: disabled ? 0.75 : 1,
            }}
          >
            {paymentDisclosureAcknowledged && <Check className="w-3 h-3 text-white" />}
          </span>
          <span className={paymentDisclosureAcknowledged ? "text-[#4A5A4E] font-semibold" : "text-[#6E665A] font-semibold"}>
            I have read this to the client and they understand and acknowledge it.
          </span>
        </label>
      </div>
    </div>
  );
}
